import React, {
  useImperativeHandle,
  useState,
  forwardRef,
  useRef,
} from 'react';
import { Drawer, Form, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  SiteScheduleModalRef,
  Schedule,
  ScheduleType,
  MonthlyType,
  ScheduleFormData,
} from './types';
import { useScheduleManagement } from './hooks/useScheduleManagement';
import ScheduleForm from './components/ScheduleForm';
import ScheduleCalendar from './components/ScheduleCalendar';
import ScheduleDetailModal from './components/ScheduleDetailModal';
import { Permission } from '../../dto';
import { useUser } from '@sezenta/connection';

const SiteScheduleModal = forwardRef<SiteScheduleModalRef>((_, ref) => {
  const [visible, setVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [siteName, setSiteName] = useState<string>('');
  const [form] = Form.useForm<ScheduleFormData>();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [selectedSchedules, setSelectedSchedules] = useState<Schedule[]>([]);
  const [scheduleDetailModal, setScheduleDetailModal] = useState(false);
  const [currentScheduleType, setCurrentScheduleType] = useState<ScheduleType>(
    ScheduleType.WEEKLY,
  );
  const [monthlyType, setMonthlyType] = useState<MonthlyType>(
    MonthlyType.ON_DATE,
  );
  const [calendarMode, setCalendarMode] = useState<'month' | 'year'>('month');
  const user = useUser(); // Assuming useUser is a custom hook to get user info

  const {
    schedules,
    loading,
    submitting,
    deleting,
    loadSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  } = useScheduleManagement();

  const promiseRef = useRef<{
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }>();

  useImperativeHandle(ref, () => ({
    open: (id: number, name: string) => {
      setSiteId(id);
      setSiteName(name);
      setVisible(true);
      loadSchedules(id);
      return new Promise<any>((resolve, reject) => {
        promiseRef.current = { resolve, reject };
      });
    },
  }));

  const handleClose = () => {
    setVisible(false);
    setShowForm(false);
    setEditingSchedule(null);
    setSiteId(null);
    setSiteName('');
    form.resetFields();
    promiseRef.current?.resolve(schedules);
  };

  const handleAddNew = () => {
    setEditingSchedule(null);
    setShowForm(true);
    setCurrentScheduleType(ScheduleType.WEEKLY);
    setMonthlyType(MonthlyType.ON_DATE);
    form.resetFields();
    form.setFieldsValue({
      scheduleType: ScheduleType.WEEKLY,
      collectionConfig: {
        weekly: { days: [] },
      },
      isActive: true,
    });

    // Scroll to top of drawer to show the form
    setTimeout(() => {
      const drawerBody = document.querySelector('.ant-drawer-body');
      if (drawerBody) {
        drawerBody.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleEdit = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setShowForm(true);
    setScheduleDetailModal(false);
    setCurrentScheduleType(schedule.scheduleType);

    if (
      schedule.scheduleType === ScheduleType.MONTHLY &&
      schedule.collectionConfig.monthly
    ) {
      setMonthlyType(schedule.collectionConfig.monthly.type);
    }

    // Set form values with dateRange for RangePicker
    form.setFieldsValue({
      scheduleType: schedule.scheduleType,
      collectionConfig: schedule.collectionConfig,
      dateRange: [
        schedule.startDate ? dayjs(schedule.startDate) : null,
        schedule.endDate ? dayjs(schedule.endDate) : null,
      ],
      description: schedule.description,
      isActive: schedule.isActive,
    } as any);

    setTimeout(() => {
      const drawerBody = document.querySelector('.ant-drawer-body');
      if (drawerBody) {
        drawerBody.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const handleSubmit = async (values: ScheduleFormData) => {
    if (!siteId) return;

    let success: undefined | boolean = false;
    if (editingSchedule) {
      success = await updateSchedule(siteId, editingSchedule.id, values);
    } else {
      success = await createSchedule(siteId, values);
    }

    if (success) {
      setShowForm(false);
      setEditingSchedule(null);
      form.resetFields();
    }
  };

  const handleDelete = async (scheduleId: string) => {
    if (siteId) {
      await deleteSchedule(siteId, scheduleId);
    }
  };

  const handleDateSelect = (
    date: dayjs.Dayjs,
    schedulesForDate: Schedule[],
  ) => {
    setSelectedDate(date);
    setSelectedSchedules(schedulesForDate);
    if (schedulesForDate.length > 0 && calendarMode !== 'year') {
      setScheduleDetailModal(true);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSchedule(null);
    form.resetFields();
  };

  return (
    <Drawer
      title={`Collection Schedules - ${siteName}`}
      open={visible}
      onClose={handleClose}
      width={800}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>Close</Button>
        </div>
      }
    >
      {user?.permissions.includes(Permission.EDIT_SITE_SCHEDULE) && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            disabled={showForm || submitting}
          >
            Add New Schedule
          </Button>
        </div>
      )}

      {showForm && (
        <ScheduleForm
          form={form}
          onFinish={handleSubmit}
          onCancel={handleFormCancel}
          submitting={submitting}
          editingSchedule={editingSchedule}
          currentScheduleType={currentScheduleType}
          setCurrentScheduleType={setCurrentScheduleType}
          monthlyType={monthlyType}
          setMonthlyType={setMonthlyType}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div
            style={{ fontSize: '16px', color: '#1890ff', marginBottom: '8px' }}
          >
            Loading schedules...
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Please wait while we fetch your collection schedules
          </div>
        </div>
      ) : schedules.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
          <div
            style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}
          >
            No schedules found
          </div>
          <div style={{ fontSize: '14px', marginBottom: '16px' }}>
            Create your first collection schedule to get started
          </div>
          {user?.permissions.includes(Permission.EDIT_SITE_SCHEDULE) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNew}
              disabled={showForm || submitting}
            >
              Add New Schedule
            </Button>
          )}
        </div>
      ) : (
        <ScheduleCalendar
          schedules={schedules}
          onDateSelect={handleDateSelect}
          calendarMode={calendarMode}
          onModeChange={setCalendarMode}
        />
      )}

      <ScheduleDetailModal
        visible={scheduleDetailModal}
        onClose={() => setScheduleDetailModal(false)}
        selectedDate={selectedDate}
        selectedSchedules={selectedSchedules}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleting={deleting}
        submitting={submitting}
      />
    </Drawer>
  );
});

SiteScheduleModal.displayName = 'SiteScheduleModal';

export const useSiteScheduleModal = () => {
  const ref = React.useRef<SiteScheduleModalRef>(null);
  return ref;
};

export default SiteScheduleModal;
